import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  AlertTriangle, CheckCircle, Loader2, Mail, Phone, Users,
  DollarSign, Receipt, Clock, RefreshCcw, ArrowRight,
} from "lucide-react";
import { getMemberClassification } from "../../lib/memberClassification";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Money({ value, className = "" }) {
  if (value === null || value === undefined || value === "") return <span className="text-slate-500">—</span>;
  const v = typeof value === "number" ? value : parseFloat(value);
  if (Number.isNaN(v)) return <span className="text-slate-500">—</span>;
  return <span className={className}>${v.toFixed(2)}</span>;
}

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-2 gap-2 py-1.5 border-b border-slate-800/50 last:border-0 text-sm">
      <span className="text-slate-400">{label}</span>
      <span className="text-white">{children}</span>
    </div>
  );
}

export default function PaymentDetailDialog({ recordId, open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  // Recalculate-fee dialog state
  const [recalcOpen, setRecalcOpen] = useState(false);
  const [recalcLoading, setRecalcLoading] = useState(false);
  const [recalcPreview, setRecalcPreview] = useState(null);
  const [applyingRecalc, setApplyingRecalc] = useState(false);

  const loadDetail = () => {
    if (!recordId) return;
    setLoading(true);
    setDetail(null);
    axios
      .get(`${API}/academy/admin/payment-detail/${recordId}`, { withCredentials: true })
      .then((r) => setDetail(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load detail"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open || !recordId) return;
    loadDetail();
  }, [open, recordId]); // eslint-disable-line

  const openRecalcPreview = async () => {
    const regId = detail?.source === "registration" ? detail?.record_id : null;
    if (!regId) {
      toast.error("Recalculate is only available from the registration-level view");
      return;
    }
    setRecalcLoading(true);
    setRecalcOpen(true);
    try {
      const res = await axios.post(
        `${API}/academy/admin/registration/${regId}/recalc-fee`,
        { confirm: false },
        { withCredentials: true }
      );
      setRecalcPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to preview recalculation");
      setRecalcOpen(false);
    } finally {
      setRecalcLoading(false);
    }
  };

  const applyRecalc = async () => {
    const regId = detail?.source === "registration" ? detail?.record_id : null;
    if (!regId) return;
    setApplyingRecalc(true);
    try {
      const res = await axios.post(
        `${API}/academy/admin/registration/${regId}/recalc-fee`,
        { confirm: true },
        { withCredentials: true }
      );
      toast.success(
        `Fee updated: $${res.data.old_total.toFixed(2)} → $${res.data.new_total.toFixed(2)}` +
          (res.data.transactions_synced ? ` · ${res.data.transactions_synced} pending transaction(s) synced` : "")
      );
      setRecalcOpen(false);
      setRecalcPreview(null);
      // Reload the detail so the admin sees the new amount immediately
      loadDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to apply recalculation");
    } finally {
      setApplyingRecalc(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-slate-950 border-slate-800" data-testid="payment-detail-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Receipt className="w-5 h-5 text-emerald-400" />
            Payment Detail
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Complete view of what this parent paid for, line-by-line.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-12 flex justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
          </div>
        )}

        {!loading && detail && (
          <div className="space-y-4">
            {/* Mismatch flags banner */}
            {detail.mismatch_flags?.length > 0 && (
              <Card className="p-3 bg-amber-500/10 border-amber-500/30" data-testid="mismatch-banner">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-200 space-y-1">
                    {detail.mismatch_flags.map((m, i) => (
                      <p key={i}>{m}</p>
                    ))}
                  </div>
                </div>
              </Card>
            )}

            {/* Parent */}
            <Card className="p-4 bg-slate-900/50">
              <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
                <Users className="w-4 h-4 text-blue-400" /> Parent
              </h3>
              <Row label="Name">{detail.parent?.name || "—"}</Row>
              <Row label="Email">
                {detail.parent?.email ? (
                  <a href={`mailto:${detail.parent.email}`} className="text-blue-400 hover:underline">
                    <Mail className="w-3 h-3 inline mr-1" />
                    {detail.parent.email}
                  </a>
                ) : "—"}
              </Row>
              <Row label="Phone">
                {detail.parent?.phone ? (
                  <><Phone className="w-3 h-3 inline mr-1" />{detail.parent.phone}</>
                ) : "—"}
              </Row>
              <Row label="SCC Member">
                {detail.parent?.is_scc_member ? (
                  <Badge className="bg-emerald-500/20 text-emerald-400">Yes {detail.parent.membership_number ? `· ${detail.parent.membership_number}` : ""}</Badge>
                ) : <span className="text-slate-500">No</span>}
              </Row>
            </Card>

            {/* Children + Sessions */}
            <Card className="p-4 bg-slate-900/50">
              <h3 className="text-white font-semibold mb-2">Children &amp; Sessions</h3>
              {detail.children?.map((c, i) => (
                <div key={i} className="py-2 border-b border-slate-800/50 last:border-0">
                  <div className="flex items-center justify-between">
                    <span className="text-white font-medium">{c.name}</span>
                    <Badge variant="outline" className="text-xs border-slate-600 text-slate-300">
                      {c.session_count} session{c.session_count === 1 ? "" : "s"}/week
                    </Badge>
                  </div>
                  {c.sessions?.length > 0 && (
                    <ul className="text-xs text-slate-400 mt-1 ml-3 space-y-0.5">
                      {c.sessions.map((s, j) => (
                        <li key={j}>
                          {s.name || s.age_group}
                          {s.start_time ? ` · ${s.start_time}` : ""}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </Card>

            {/* Fee breakdown */}
            <Card className="p-4 bg-slate-900/50">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-white font-semibold flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-emerald-400" /> Fee Breakdown
                </h3>
                {detail.source === "registration" && detail.payment?.status !== "approved" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={openRecalcPreview}
                    className="h-7 px-2 text-xs border-amber-500/50 text-amber-300 hover:bg-amber-500/10"
                    data-testid="recalc-fee-btn"
                    title="Recalculate using the parent's current member status"
                  >
                    <RefreshCcw className="w-3 h-3 mr-1" />
                    Recalculate fee
                  </Button>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-xs text-slate-400 uppercase mb-1">From registration form</p>
                  <Row label="Total amount">
                    <Money value={detail.registration?.total_amount} className="font-bold" />
                  </Row>
                  <Row label="Digital membership">
                    {detail.registration?.fee_breakdown_on_registration?.digital_membership_purchased
                      ? <>Yes · <Money value={detail.registration.fee_breakdown_on_registration.digital_membership_price} /></>
                      : <span className="text-slate-500">No</span>}
                  </Row>
                  {(detail.registration?.fee_breakdown_on_registration?.registration_fees?.length > 0) && (
                    <Row label="Registration fees">
                      {detail.registration.fee_breakdown_on_registration.registration_fees.map((r, i) => (
                        <div key={i}>{r.name || "Reg fee"}: <Money value={r.amount || r.price} /></div>
                      ))}
                    </Row>
                  )}
                </div>

                {detail.payment?.fee_breakdown_from_transaction ? (
                  <div>
                    <p className="text-xs text-slate-400 uppercase mb-1">From actual payment transaction</p>
                    <Row label="Base fee (term × sessions)"><Money value={detail.payment.fee_breakdown_from_transaction.base_fee} /></Row>
                    <Row label="Registration fee"><Money value={detail.payment.fee_breakdown_from_transaction.registration_fee} /></Row>
                    <Row label="Digital membership"><Money value={detail.payment.fee_breakdown_from_transaction.dm_price} /></Row>
                    <Row label="Sibling discount">
                      {detail.payment.fee_breakdown_from_transaction.sibling_discount ? (
                        <span className="text-emerald-400">− <Money value={detail.payment.fee_breakdown_from_transaction.sibling_discount} /></span>
                      ) : <Money value={0} />}
                    </Row>
                    <Row label="Children / term weeks">
                      {detail.payment.fee_breakdown_from_transaction.children_count || 1} / {detail.payment.fee_breakdown_from_transaction.term_weeks || "—"}
                    </Row>
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 italic">No fee breakdown available from payment transaction</p>
                )}
              </div>
            </Card>

            {/* Payment */}
            <Card className="p-4 bg-slate-900/50">
              <h3 className="text-white font-semibold mb-2">Payment</h3>
              <Row label="Status">
                <Badge className={
                  detail.payment?.status === "approved" ? "bg-emerald-500/20 text-emerald-400"
                    : detail.payment?.status === "pending" ? "bg-amber-500/20 text-amber-400"
                    : "bg-slate-500/20 text-slate-300"
                }>
                  {detail.payment?.status === "approved" && <CheckCircle className="w-3 h-3 inline mr-1" />}
                  {detail.payment?.status === "pending" && <Clock className="w-3 h-3 inline mr-1" />}
                  {detail.payment?.status}
                </Badge>
              </Row>
              <Row label="Method">{detail.payment?.method || "—"}</Row>
              <Row label="Reference">
                {detail.payment?.reference ? <code className="text-xs bg-slate-800 px-1 py-0.5 rounded">{detail.payment.reference}</code> : "—"}
              </Row>
              <Row label="Amount expected"><Money value={detail.payment?.amount_expected} /></Row>
              <Row label="Amount paid">
                <Money value={detail.payment?.amount_paid} className={detail.payment?.amount_paid > 0 ? "font-bold text-emerald-400" : ""} />
              </Row>
              {detail.payment?.amount_diff !== null && detail.payment?.amount_diff !== undefined && (
                <Row label="Difference">
                  {Math.abs(detail.payment.amount_diff) < 0.01 ? (
                    <Badge className="bg-emerald-500/20 text-emerald-400">Exact match</Badge>
                  ) : (
                    <Badge className={detail.payment.amount_diff > 0 ? "bg-blue-500/20 text-blue-400" : "bg-red-500/20 text-red-400"}>
                      {detail.payment.amount_diff > 0 ? "Overpaid" : "Underpaid"}: <Money value={Math.abs(detail.payment.amount_diff)} />
                    </Badge>
                  )}
                </Row>
              )}
              <Row label="Paid at">
                {detail.payment?.paid_at ? new Date(detail.payment.paid_at).toLocaleString() : "—"}
              </Row>
            </Card>

            {/* Matched transactions */}
            {detail.payment?.matched_transactions?.length > 1 && (
              <Card className="p-4 bg-slate-900/50">
                <h3 className="text-white font-semibold mb-2">Other matching transactions ({detail.payment.matched_transactions.length - 1})</h3>
                <ul className="text-xs text-slate-400 space-y-1">
                  {detail.payment.matched_transactions.slice(1).map((t) => (
                    <li key={t.id}>
                      <code>{t.reference}</code> · <Money value={t.amount} /> · {t.status} · {t.paid_at ? new Date(t.paid_at).toLocaleDateString() : "no date"}
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </div>
        )}

        <div className="pt-2">
          <Button onClick={onClose} variant="outline" className="w-full border-slate-700" data-testid="close-detail-btn">
            Close
          </Button>
        </div>
      </DialogContent>

      {/* Recalculate Fee Preview Dialog */}
      <Dialog open={recalcOpen} onOpenChange={(v) => !v && setRecalcOpen(false)}>
        <DialogContent
          className="max-w-2xl bg-slate-950 border-slate-800 max-h-[90vh] overflow-y-auto"
          data-testid="recalc-preview-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <RefreshCcw className="w-5 h-5 text-amber-400" />
              Recalculate Fee
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Re-runs the fee calculator using the parent's <strong>current</strong> member status.
              Use this when a parent's SCC or Digital membership was verified AFTER they registered.
            </DialogDescription>
          </DialogHeader>

          {recalcLoading && (
            <div className="py-10 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-amber-400" />
              <span className="ml-2 text-slate-400">Computing…</span>
            </div>
          )}

          {!recalcLoading && recalcPreview && (
            <div className="space-y-4">
              {/* Parent status comparison */}
              <Card className="p-3 bg-slate-900/60 border-slate-700">
                <p className="text-xs text-slate-400 uppercase mb-2">Parent member status</p>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <p className="text-slate-500 mb-1">At time of registration</p>
                    <Badge className="bg-slate-700/50 text-slate-300">
                      {recalcPreview.registration_stored_status?.is_scc_member ? "SCC Member" : "Non-member"}
                    </Badge>
                    <p className="text-slate-400 mt-1">
                      {recalcPreview.registration_stored_status?.member_type || "—"}
                      {recalcPreview.registration_stored_status?.membership_number &&
                        ` · ${recalcPreview.registration_stored_status.membership_number}`}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500 mb-1">Current (will be applied)</p>
                    {(() => {
                      const cls = getMemberClassification(recalcPreview.parent_current_status || {});
                      return (
                        <div className="flex items-center gap-1 flex-wrap">
                          <Badge className={cls.colorClass}>{cls.label}</Badge>
                          {(cls.secondaryTags || []).map((t) => (
                            <span
                              key={t.label}
                              className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${t.colorClass}`}
                              title="Active Digital Membership"
                            >
                              {t.label}
                            </span>
                          ))}
                        </div>
                      );
                    })()}
                    <p className="text-slate-400 mt-1">
                      {recalcPreview.parent_current_status?.member_type || "—"}
                      {recalcPreview.parent_current_status?.membership_number &&
                        ` · ${recalcPreview.parent_current_status.membership_number}`}
                    </p>
                  </div>
                </div>
              </Card>

              {/* Delta */}
              <Card className={`p-4 ${recalcPreview.delta < 0 ? "bg-emerald-500/10 border-emerald-500/40" : recalcPreview.delta > 0 ? "bg-amber-500/10 border-amber-500/40" : "bg-slate-900/60 border-slate-700"}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-400 uppercase">Old total</p>
                    <p className="text-2xl font-bold text-slate-300">
                      ${(recalcPreview.old_total || 0).toFixed(2)}
                    </p>
                  </div>
                  <ArrowRight className="w-6 h-6 text-slate-500" />
                  <div className="text-right">
                    <p className="text-xs text-slate-400 uppercase">New total</p>
                    <p className={`text-2xl font-bold ${recalcPreview.delta < 0 ? "text-emerald-300" : recalcPreview.delta > 0 ? "text-amber-300" : "text-white"}`} data-testid="recalc-new-total">
                      ${(recalcPreview.new_total || 0).toFixed(2)}
                    </p>
                  </div>
                </div>
                <div className="mt-3 text-sm text-center">
                  <span className={recalcPreview.delta < 0 ? "text-emerald-300" : recalcPreview.delta > 0 ? "text-amber-300" : "text-slate-400"}>
                    {recalcPreview.delta === 0
                      ? "No change"
                      : `${recalcPreview.delta < 0 ? "Reduction" : "Increase"}: $${Math.abs(recalcPreview.delta).toFixed(2)}`}
                  </span>
                </div>
              </Card>

              {/* Line items */}
              {recalcPreview.new_line_items?.length > 0 && (
                <Card className="p-3 bg-slate-900/40 border-slate-700">
                  <p className="text-xs text-slate-400 uppercase mb-2">New breakdown per child</p>
                  <div className="space-y-1 text-sm">
                    {recalcPreview.new_line_items.map((li, i) => (
                      <div key={i} className="flex justify-between py-1 border-b border-slate-800/50 last:border-0">
                        <span className="text-slate-300">
                          {li.child_name}
                          <span className="text-slate-500 text-xs ml-1">({li.sessions_per_week}/wk × {recalcPreview.term_weeks} wks)</span>
                          {li.discount_pct > 0 && (
                            <span className="ml-2 text-xs text-emerald-400">
                              −{li.discount_pct.toFixed(0)}% member
                            </span>
                          )}
                        </span>
                        <span className="font-mono text-white">
                          ${li.child_total.toFixed(2)}
                          {li.registration_fee > 0 && (
                            <span className="text-xs text-slate-500 ml-1">(incl. ${li.registration_fee.toFixed(2)} reg)</span>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              <p className="text-xs text-slate-500 italic">
                Confirming will update the registration's total, regenerate the fee breakdown, and sync any pending
                payment transaction(s) so the parent's PayNow QR reflects the new amount.
              </p>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setRecalcOpen(false)}
              className="border-slate-700"
              data-testid="recalc-cancel-btn"
              disabled={applyingRecalc}
            >
              Cancel
            </Button>
            <Button
              onClick={applyRecalc}
              disabled={recalcLoading || applyingRecalc || !recalcPreview}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="recalc-apply-btn"
            >
              {applyingRecalc ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Apply new fee
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
